import { getTimezoneOffset } from 'date-fns-tz';
import * as R from 'ramda';
import { convert12hrTo24hr, convert24hrTo12hr } from '../../../../utils/time-utils';
import { defaultTimeframe, WtoRule } from '../../wto-form-context';
import {
  capitalize,
  formatTimestampOffset,
  parseTimestampTimeframe,
  setFormattedTime,
  setTimestampTime
} from '../../wto-utils';
import { MILLISECONDS_IN_HOUR, TIMEZONE_MAP } from './rules-form-modal-constants';

export const createTimeframeHandlers = (
  newRule: WtoRule,
  setNewRule: (rule: WtoRule) => void,
  savedTimeframe: ReturnType<typeof parseTimestampTimeframe>
) => {
  const toggleTimeframe = () => {
    const newRuleFormObj = R.clone(newRule);
    newRuleFormObj.timeframe_enabled = !newRuleFormObj.timeframe_enabled;
    if (newRuleFormObj.timeframe_enabled) {
      newRuleFormObj.timeframe = { ...defaultTimeframe };
      setNewRule(newRuleFormObj);
    } else {
      const { timeframe: _timeframe, ...rest } = newRuleFormObj;
      setNewRule(rest);
    }
  };

  const toggleTimeframeParam = () => {
    const newRuleFormObj = R.clone(newRule);
    if (!newRuleFormObj.timeframe) return;
    newRuleFormObj.timeframe.timeframe_utm_test_id_enabled =
      !newRuleFormObj.timeframe.timeframe_utm_test_id_enabled;
    setNewRule(newRuleFormObj);
  };

  const updateTimeframeId = (value: string) => {
    const newRuleFormObj = R.clone(newRule);
    if (!newRuleFormObj.timeframe) return;
    newRuleFormObj.timeframe.utm_test_id = value;
    setNewRule(newRuleFormObj);
  };

  const handleTimeframeDates = (dates: { startDate: string; endDate: string }) => {
    const newRuleFormObj = R.clone(newRule);
    if (!newRuleFormObj.timeframe) return;

    const { startDate, endDate } = dates;

    const newStartValues = { ...savedTimeframe.start };
    const newEndValues = { ...savedTimeframe.end };

    const [startYear, startMonth, startDay] = startDate.split('-');
    const [endYear, endMonth, endDay] = endDate.split('-');

    newStartValues.year = startYear;
    newStartValues.month = startMonth;
    newStartValues.day = startDay;
    newEndValues.year = endYear;
    newEndValues.month = endMonth;
    newEndValues.day = endDay;

    const startOffset =
      getTimezoneOffset(
        TIMEZONE_MAP[newStartValues.timezone],
        new Date(setTimestampTime(newStartValues))
      ) / MILLISECONDS_IN_HOUR;
    const endOffset =
      getTimezoneOffset(
        TIMEZONE_MAP[newStartValues.timezone],
        new Date(setTimestampTime(newEndValues))
      ) / MILLISECONDS_IN_HOUR;

    newStartValues.offset = formatTimestampOffset(startOffset);
    newEndValues.offset = formatTimestampOffset(endOffset);

    newRuleFormObj.timeframe.startDateTime = setTimestampTime(newStartValues);
    newRuleFormObj.timeframe.formattedStartDateTime = setFormattedTime(newStartValues);
    newRuleFormObj.timeframe.endDateTime = setTimestampTime(newEndValues);
    newRuleFormObj.timeframe.formattedEndDateTime = setFormattedTime(newEndValues);

    setNewRule(newRuleFormObj);
  };

  const handleTimeframeTimezone = (timezone: string | null) => {
    if (timezone) {
      const newRuleFormObj = R.clone(newRule);
      if (!newRuleFormObj.timeframe) return;

      const newStartValues = { ...savedTimeframe.start };
      const newEndValues = { ...savedTimeframe.end };

      const startOffset =
        getTimezoneOffset(
          TIMEZONE_MAP[timezone],
          new Date(
            !newRuleFormObj.timeframe.startDateTime
              ? setTimestampTime(newStartValues)
              : newRuleFormObj.timeframe.startDateTime
          )
        ) / MILLISECONDS_IN_HOUR;
      const endOffset =
        getTimezoneOffset(
          TIMEZONE_MAP[timezone],
          new Date(
            !newRuleFormObj.timeframe.endDateTime
              ? setTimestampTime(newEndValues)
              : newRuleFormObj.timeframe.endDateTime
          )
        ) / MILLISECONDS_IN_HOUR;

      newStartValues.offset = formatTimestampOffset(startOffset);
      newEndValues.offset = formatTimestampOffset(endOffset);

      newRuleFormObj.timeframe.time_zone = timezone;
      newRuleFormObj.timeframe.startDateTime = setTimestampTime(newStartValues);
      newRuleFormObj.timeframe.formattedStartDateTime = setFormattedTime(newStartValues);
      newRuleFormObj.timeframe.endDateTime = setTimestampTime(newEndValues);
      newRuleFormObj.timeframe.formattedEndDateTime = setFormattedTime(newEndValues);

      setNewRule(newRuleFormObj);
    }
  };

  const handleTimeframeTime = (
    value: string | null,
    type: 'hour' | 'minute',
    bound: 'start' | 'end'
  ) => {
    if (value) {
      const newRuleFormObj = R.clone(newRule);
      if (!newRuleFormObj.timeframe) return;

      const newValues = { ...savedTimeframe[bound] };

      newValues.hour12 = type === 'hour' ? Number(value).toString() : savedTimeframe[bound].hour12;
      newValues.minute = type === 'minute' ? value.padStart(2, '0') : savedTimeframe[bound].minute;

      [newValues.hour24] = convert12hrTo24hr(
        `${newValues.hour12}:${newValues.minute}${savedTimeframe[bound].period}`
      ).split(':');

      newRuleFormObj.timeframe[`${bound}DateTime`] = setTimestampTime(newValues);
      newRuleFormObj.timeframe[`formatted${capitalize(bound)}DateTime`] =
        setFormattedTime(newValues);

      setNewRule(newRuleFormObj);
    }
  };

  const handleTimeframePeriod = (period: 'am' | 'pm' | null, bound: 'start' | 'end') => {
    if (period) {
      const newRuleFormObj = R.clone(newRule);
      if (!newRuleFormObj.timeframe) return;

      const newValues = { ...savedTimeframe[bound] };

      newValues.period = period;

      if (period === 'am' && newValues.hour24 >= 12) {
        newValues.hour24 = (Number(newValues.hour24) - 12).toString().padStart(2, '0');
      } else if (period === 'pm' && newValues.hour24 < 12) {
        newValues.hour24 = (Number(newValues.hour24) + 12).toString().padStart(2, '0');
      }

      [newValues.hour12] = convert24hrTo12hr(`${newValues.hour24}:${newValues.minute}`).split(':');

      newRuleFormObj.timeframe[`${bound}DateTime`] = setTimestampTime(newValues);
      newRuleFormObj.timeframe[`formatted${capitalize(bound)}DateTime`] =
        setFormattedTime(newValues);

      setNewRule(newRuleFormObj);
    }
  };

  return {
    toggleTimeframe,
    toggleTimeframeParam,
    updateTimeframeId,
    handleTimeframeDates,
    handleTimeframeTimezone,
    handleTimeframeTime,
    handleTimeframePeriod
  };
};
